/**
 * TaskChecklistTemplatesManager - Página de gestão dos Templates de Checklist de Tarefas
 *
 * Permite ao administrador do tenant listar, criar, editar, excluir e definir
 * a visibilidade dos templates (via ManageVisibilityDialog compartilhado).
 *
 * Reaproveita:
 *  - useTaskChecklistTemplates* (hooks Fase 3)
 *  - ManageVisibilityDialog (padrão unificado de visibilidade)
 *  - ListPageLayout family (padrão de listagens do sistema)
 *
 * @version 1.0.0
 * RELEASE: 2026-07-07 v1.0.0 - Versão inicial da Fase 3.
 */
import { useState, useMemo } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  Eye,
  Lock,
  Users,
  Globe2,
  Tags,
  ListChecks,
  X,
  GripVertical,
  Info,
  Search,
  MoreHorizontal,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { StatusDot } from '@/components/tenant/SettingsInfoRow';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import ManageVisibilityDialog from '@/components/shared/ManageVisibilityDialog';
import { FormSectionCard } from '@/components/crm/deals/components/FormSectionCard';
import {
  useTaskChecklistTemplatesList,
  useTaskChecklistTemplateDetail,
  useSaveTaskChecklistTemplate,
  useDeleteTaskChecklistTemplate,
  type TaskChecklistTemplateSummary,
} from '@/hooks/useTaskChecklistTemplates';
import { cn } from '@/lib/utils';


/** Ícone e label da visibilidade */
const VISIBILITY_META: Record<
  TaskChecklistTemplateSummary['visibility'],
  { label: string; icon: React.ComponentType<{ className?: string }> }
> = {
  private: { label: 'Privado', icon: Lock },
  shared: { label: 'Compartilhado', icon: Users },
  by_profile: { label: 'Por perfil', icon: Tags },
  public: { label: 'Todos', icon: Globe2 },
};

export default function TaskChecklistTemplatesManager() {
  const { data: templates, isLoading } = useTaskChecklistTemplatesList();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const [visibilityId, setVisibilityId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Busca local por nome/descrição (padrão TaskPhases)
  const [search, setSearch] = useState('');

  const deleteMutation = useDeleteTaskChecklistTemplate();

  const openNew = () => {
    setEditingId(null);
    setEditorOpen(true);
  };
  const openEdit = (id: string) => {
    setEditingId(id);
    setEditorOpen(true);
  };

  const templateBeingDeleted = useMemo(
    () => templates?.find((t) => t.id === confirmDeleteId),
    [templates, confirmDeleteId],
  );

  // Templates filtrados pela busca (nome ou descrição)
  const filtered = useMemo(() => {
    const list = templates ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        (t.description ?? '').toLowerCase().includes(q),
    );
  }, [templates, search]);

  return (
    <div className="flex flex-col h-full">
      {/* Header Compacto (padrão TaskPhases/Contatos) */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b">
        <div>
          <h1 className="text-xl font-semibold">Templates de Checklist</h1>
          <p className="text-xs text-muted-foreground">
            Padronize listas de verificação reutilizáveis nas suas tarefas.
          </p>
        </div>
        <Button
          size="xs"
          onClick={openNew}
          className="hover:shadow-md hover:scale-[1.02] transition-all duration-200"
        >
          <Plus className="mr-1.5" />
          Novo template
        </Button>
      </div>

      <div className="px-4 py-4 space-y-4">
        {/* Filtros — busca por nome/descrição */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar templates..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* Tabela / Estado vazio (padrão TaskPhases) */}
        {isLoading ? (
          <Card>
            <div className="p-4 space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={`sk-${i}`} className="h-10 w-full" />
              ))}
            </div>
          </Card>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <ListChecks className="h-12 w-12 mb-4 opacity-50" />
              <p className="text-lg font-medium">Nenhum template encontrado</p>
              <p className="text-sm mt-1">
                {search
                  ? 'Tente ajustar sua busca'
                  : 'Crie o primeiro template para começar'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Visibilidade</TableHead>
                  <TableHead className="w-24 text-center">Listas</TableHead>
                  <TableHead className="w-24 text-center">Itens</TableHead>
                  <TableHead className="w-24 text-center">Ativo</TableHead>
                  <TableHead className="w-16" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((t) => {
                  const Meta = VISIBILITY_META[t.visibility];
                  const Icon = Meta.icon;
                  return (
                    <TableRow key={t.id}>
                      <TableCell>
                        <div className="font-medium">{t.name}</div>
                        {t.description && (
                          <div className="text-xs text-muted-foreground truncate max-w-md">
                            {t.description}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="gap-1">
                          <Icon className="h-3 w-3" />
                          {Meta.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">{t.group_count}</TableCell>
                      <TableCell className="text-center">{t.item_count}</TableCell>
                      {/* Status — padrão unificado (Badge outline + StatusDot) */}
                      <TableCell className="text-center">
                        <Badge
                          variant="outline"
                          className="h-5 gap-1.5 px-1.5 text-[10px] font-medium uppercase tracking-wider"
                        >
                          <StatusDot tone={t.is_active ? 'emerald' : 'muted'} />
                          {t.is_active ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEdit(t.id)}>
                              <Pencil className="h-4 w-4 mr-2" /> Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setVisibilityId(t.id)}>
                              <Eye className="h-4 w-4 mr-2" /> Visibilidade
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={() => setConfirmDeleteId(t.id)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" /> Excluir
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        )}

        {/* Editor */}
        <TemplateEditorDialog
          open={editorOpen}
          onOpenChange={setEditorOpen}
          templateId={editingId}
        />

        {/* Visibilidade unificada */}
        <ManageVisibilityDialog
          open={!!visibilityId}
          onOpenChange={(o) => !o && setVisibilityId(null)}
          entityType="task_checklist_template"
          entityId={visibilityId ?? undefined}
          entityLabel={templates?.find((t) => t.id === visibilityId)?.name}
        />

        {/* Confirmação de exclusão */}
        <ConfirmDialog
          open={!!confirmDeleteId}
          onOpenChange={(o) => !o && setConfirmDeleteId(null)}
          title="Excluir template?"
          description={
            templateBeingDeleted
              ? `O template "${templateBeingDeleted.name}" será removido definitivamente.`
              : 'O template será removido definitivamente.'
          }
          confirmText="Excluir"
          variant="danger"
          onConfirm={async () => {
            if (!confirmDeleteId) return;
            await deleteMutation.mutateAsync(confirmDeleteId);
            setConfirmDeleteId(null);
          }}
        />
      </div>
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/* Editor de template (nome, descrição, ativo, grupos e itens)                */
/* -------------------------------------------------------------------------- */

interface TemplateEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateId: string | null;
}

interface DraftItem { key: string; content: string }
interface DraftGroup { key: string; title: string; items: DraftItem[] }

function TemplateEditorDialog({ open, onOpenChange, templateId }: TemplateEditorDialogProps) {
  const { data: detail, isLoading } = useTaskChecklistTemplateDetail(templateId, open);
  const saveMutation = useSaveTaskChecklistTemplate();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [groups, setGroups] = useState<DraftGroup[]>([]);

  // Sincroniza estado quando o detail chega ou quando abre em modo "novo"
  useMemo(() => {
    if (!open) return;
    if (templateId && detail) {
      setName(detail.name);
      setDescription(detail.description ?? '');
      setIsActive(detail.is_active);
      setGroups(
        detail.groups.map((g) => ({
          key: g.id,
          title: g.title,
          items: g.items.map((i) => ({ key: i.id, content: i.content })),
        })),
      );
    } else if (!templateId) {
      setName('');
      setDescription('');
      setIsActive(true);
      setGroups([]);
    }
  }, [open, templateId, detail]);

  const addGroup = () => {
    setGroups((prev) => [
      ...prev,
      { key: `g-${Date.now()}-${Math.random()}`, title: '', items: [] },
    ]);
  };
  const removeGroup = (key: string) => setGroups((prev) => prev.filter((g) => g.key !== key));
  const updateGroup = (key: string, patch: Partial<DraftGroup>) =>
    setGroups((prev) => prev.map((g) => (g.key === key ? { ...g, ...patch } : g)));

  const addItem = (groupKey: string) => {
    updateGroup(groupKey, {
      items: [
        ...(groups.find((g) => g.key === groupKey)?.items ?? []),
        { key: `i-${Date.now()}-${Math.random()}`, content: '' },
      ],
    });
  };
  const updateItem = (groupKey: string, itemKey: string, content: string) => {
    const group = groups.find((g) => g.key === groupKey);
    if (!group) return;
    updateGroup(groupKey, {
      items: group.items.map((i) => (i.key === itemKey ? { ...i, content } : i)),
    });
  };
  const removeItem = (groupKey: string, itemKey: string) => {
    const group = groups.find((g) => g.key === groupKey);
    if (!group) return;
    updateGroup(groupKey, { items: group.items.filter((i) => i.key !== itemKey) });
  };

  const canSave = name.trim().length > 0 && !saveMutation.isPending;

  const handleSave = async () => {
    await saveMutation.mutateAsync({
      templateId: templateId ?? undefined,
      name: name.trim(),
      description: description.trim() || null,
      isActive,
      groups: groups
        .map((g) => ({
          title: g.title.trim(),
          items: g.items
            .map((i) => ({ content: i.content.trim() }))
            .filter((i) => i.content.length > 0),
        }))
        .filter((g) => g.title.length > 0),
    });
    onOpenChange(false);
  };

  // Totais para o painel de resumo
  const totalGroups = groups.length;
  const totalItems = groups.reduce((acc, g) => acc + g.items.length, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-4xl p-0 gap-0 overflow-hidden max-h-[90vh] h-[90dvh] sm:h-auto flex flex-col"
        hideCloseButton={false}
      >
        {/* ===== HEADER PREMIUM (padrão ContactNoteEditorDialog) ===== */}
        <div className="px-6 py-4 border-b bg-gradient-to-r from-primary/5 via-primary/3 to-transparent">
          <DialogHeader>
            <div className="flex items-center justify-between gap-2">
              <DialogTitle className="flex items-center gap-2 text-base">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10">
                  <ListChecks className="h-3.5 w-3.5 text-primary" />
                </div>
                {templateId ? 'Editar template' : 'Novo template'}
              </DialogTitle>
              {templateId && (
                <Badge variant="secondary" className="gap-1">
                  {totalGroups} grupos · {totalItems} itens
                </Badge>
              )}
            </div>
            <DialogDescription className="text-xs text-muted-foreground">
              Defina o nome, a descrição e os grupos/itens que compõem o template.
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* ===== CORPO SPLIT-PANE ===== */}
        {isLoading && templateId ? (
          <div className="flex-1 p-6 space-y-3">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr,320px] min-h-0 flex-1">
            {/* Painel esquerdo — formulário */}
            <ScrollArea className="max-h-[calc(90vh-180px)]">
              <div className="p-5 space-y-4">
                {/* Bloco: Dados básicos */}
                <FormSectionCard title="Dados básicos" icon={Info}>
                  <div className="grid gap-3 md:grid-cols-[1fr_auto]">
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-muted-foreground">Nome*</label>
                      <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        maxLength={200}
                        placeholder="Ex.: Onboarding de cliente"
                      />
                    </div>
                    <div className="flex items-end">
                      <div className="flex items-center gap-2 rounded-md border bg-background px-3 py-2 h-10">
                        <Switch checked={isActive} onCheckedChange={setIsActive} />
                        <span className="text-sm">Ativo</span>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground">Descrição</label>
                    <Textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={2}
                      placeholder="Explique quando aplicar este template (opcional)"
                    />
                  </div>
                </FormSectionCard>

                {/* Bloco: Grupos e itens */}
                <FormSectionCard title="Grupos e itens" icon={ListChecks}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                      Arraste para reordenar. Grupos vazios são descartados ao salvar.
                    </span>
                    <Button size="sm" variant="outline" onClick={addGroup}>
                      <Plus className="h-3.5 w-3.5 mr-1.5" /> Adicionar grupo
                    </Button>
                  </div>

                  <div className="space-y-3">
                    {groups.length === 0 && (
                      <p className="text-sm text-muted-foreground italic py-6 text-center">
                        Nenhum grupo adicionado ainda.
                      </p>
                    )}
                    {groups.map((g, idx) => (
                      <div key={g.key} className="rounded-lg border bg-background p-3 space-y-2">
                        <div className="flex items-center gap-2">
                          <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
                          <Input
                            value={g.title}
                            onChange={(e) => updateGroup(g.key, { title: e.target.value })}
                            placeholder={`Título do grupo #${idx + 1}`}
                            maxLength={200}
                            className="flex-1"
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeGroup(g.key)}
                            title="Remover grupo"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>

                        <div className="pl-6 space-y-1.5">
                          {g.items.map((it) => (
                            <div key={it.key} className="flex items-center gap-2">
                              <span className="text-muted-foreground text-xs">•</span>
                              <Input
                                value={it.content}
                                onChange={(e) => updateItem(g.key, it.key, e.target.value)}
                                placeholder="Item…"
                                maxLength={500}
                                className={cn('h-8 text-sm')}
                              />
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => removeItem(g.key, it.key)}
                              >
                                <X className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          ))}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => addItem(g.key)}
                            className="text-muted-foreground"
                          >
                            <Plus className="h-3.5 w-3.5 mr-1.5" /> Adicionar item
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </FormSectionCard>
              </div>
            </ScrollArea>

            {/* Painel direito — resumo */}
            <div className="border-t lg:border-t-0 lg:border-l flex flex-col min-h-0 bg-muted/10">
              <div className="flex items-center gap-2 px-4 py-3 border-b bg-background/80 backdrop-blur-sm flex-shrink-0">
                <Eye className="h-3.5 w-3.5 text-primary" />
                <span className="text-xs font-medium">Resumo</span>
              </div>
              <ScrollArea className="flex-1">
                <div className="p-4 space-y-3">
                  <div className="rounded-lg border bg-background p-3 space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Grupos</span>
                      <span className="font-semibold tabular-nums">{totalGroups}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Itens</span>
                      <span className="font-semibold tabular-nums">{totalItems}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm pt-2 border-t">
                      <span className="text-muted-foreground">Status</span>
                      <Badge
                        variant="outline"
                        className="h-5 gap-1.5 px-1.5 text-[10px] font-medium uppercase tracking-wider"
                      >
                        <StatusDot tone={isActive ? 'emerald' : 'muted'} />
                        {isActive ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </div>
                  </div>

                  <div className="rounded-lg border border-dashed bg-background/50 p-3">
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      Templates <strong className="text-foreground">ativos</strong> aparecem
                      no botão "Aplicar template" da Lista de Verificação em cada tarefa.
                      A visibilidade é gerenciada separadamente pelo menu da linha.
                    </p>
                  </div>
                </div>
              </ScrollArea>
            </div>
          </div>
        )}

        {/* ===== FOOTER ===== */}
        <div className="flex flex-wrap items-center justify-end gap-2 px-6 py-3 border-t bg-muted/10">
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-3"
            onClick={() => onOpenChange(false)}
          >
            Cancelar
          </Button>
          <Button
            size="sm"
            className="h-7 px-3"
            onClick={handleSave}
            disabled={!canSave}
          >
            {saveMutation.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
